import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { formatEther } from 'viem';

import {environment} from "../../../environments/environment";

import { Web3Service } from '@/services/web3.service';
import { ThemeService } from '@/services/theme.service';
import { StateService } from '@/services/state.service';
import { MessagingService } from '@/services/messaging.service';

import {SearchComponent} from "@/components/search/search.component";

import { WalletAddressDirective } from '@/directives/wallet-address.directive';
import { TippyDirective } from '@/directives/tippy.directive';

import { firstValueFrom, map, BehaviorSubject, combineLatest, shareReplay, Subject, takeUntil, distinctUntilChanged } from 'rxjs';
import { HeaderIconsComponent } from "../header-icons/header-icons.component";
import { DataService } from '@/services/data.service';

type SignatureStatus = 'signed' | 'unsigned' | 'disconnected';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    WalletAddressDirective,
    SearchComponent,
    TippyDirective,
    HeaderIconsComponent
],
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})

export class HeaderComponent implements OnInit, OnDestroy {
  
  protected readonly environment = environment;

  private manualSearchBarState = new BehaviorSubject<boolean>(false);
  
  searchBarShown$ = combineLatest([
    this.breakpointObserver.observe([Breakpoints.Handset])
      .pipe(map(result => !result.matches)),
    this.manualSearchBarState
  ]).pipe(
    map(([isDesktop, manualState]) => isDesktop || manualState)
  );

  isDesktop$ = this.breakpointObserver
    .observe([Breakpoints.Web])
    .pipe(
      map(result => result.matches),
      shareReplay({ refCount: true, bufferSize: 1 })
    );  

  toggleSearchBar(): void {
    const currentValue = this.manualSearchBarState.value;
    this.manualSearchBarState.next(!currentValue);
  }

  // Observable for total unique addresses count
  totalDelegations$: Observable<number> = combineLatest([
    this.stateSvc.outgoingDelegations$,
    this.stateSvc.incomingDelegations$
  ]).pipe(
    map(([outgoing, incoming]) => {
      // Compter les adresses uniques
      const uniqueAddresses = new Set<string>();

      // Ajouter les adresses "to" des délégations sortantes
      outgoing.forEach(delegation => uniqueAddresses.add(delegation.to.toLowerCase()));

      // Ajouter les adresses "from" des délégations entrantes
      incoming.forEach(delegation => uniqueAddresses.add(delegation.from.toLowerCase()));

      return uniqueAddresses.size;
    })
  );

  incomingDelegationsCount$: Observable<number> = this.stateSvc.incomingDelegations$.pipe(
    map((incoming) => new Set(incoming.map(delegation => delegation.from.toLowerCase())).size),
    shareReplay({ refCount: true, bufferSize: 1 })
  );
  signatureStatus$: Observable<SignatureStatus> = combineLatest([
    this.stateSvc.web3Connected$,
    this.stateSvc.jwtToken$,
    this.stateSvc.jwtTokenExpiry$
  ]).pipe(
    map(([isConnected, token, expiry]) => {
      if (!isConnected) {
        return 'disconnected';
      }

      const hasValidToken = !!token && !!expiry && expiry > Date.now();
      return hasValidToken ? 'signed' : 'unsigned';
    }),
    distinctUntilChanged(),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  delegationTooltipContent = '';
  signatureStatusTooltipContent = '';
  stashWithdrawalAmountDisplay = '0.0000';
  hasStashWithdrawal = false;
  private destroy$ = new Subject<void>();

  constructor(
    public web3Svc: Web3Service,
    public stateSvc: StateService,
    public dataSvc: DataService,
    public themeSvc: ThemeService,
    public msgSvc: MessagingService,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
  ) {}

  ngOnInit(): void {
    console.log('this.environment', this.environment);
    
    // Expose router globally for tooltip navigation
    (window as any).angularRouter = this.router;

    combineLatest([
      this.stateSvc.incomingDelegations$,
      this.stateSvc.outgoingDelegations$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([incoming, outgoing]) => {
        this.delegationTooltipContent = this.buildDelegationTooltipContent(incoming, outgoing);
      });

    combineLatest([
      this.stateSvc.web3Connected$,
      this.stateSvc.walletAddress$
    ])
      .pipe(
        distinctUntilChanged((prev, curr) => prev[0] === curr[0] && prev[1] === curr[1]),
        takeUntil(this.destroy$)
      )
      .subscribe(([isConnected, walletAddress]) => {
        if (!isConnected || !walletAddress || this.environment.featureFlags?.STASH_BID_SUPPORT === false) {
          this.stashWithdrawalAmountDisplay = '0.0000';
          this.hasStashWithdrawal = false;
          return;
        }

        void this.refreshStashWithdrawalAmount(walletAddress);
      });

    this.signatureStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.signatureStatusTooltipContent = this.buildSignatureStatusTooltip(status);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  connect(): void {
    if (this.stateSvc.getWeb3Connected()) this.web3Svc.disconnectWeb3();
    else this.web3Svc.connect();
  }

  setMenuState(): void {
    this.stateSvc.setMenuOpen(!this.stateSvc.getMenuOpen());
  }

  refresh() {
    this.dataSvc.getMarketData()
  }

  openSignatureExplanationModal(incomingDelegationsCount: number): void {
    if (incomingDelegationsCount < 1) return;
    this.web3Svc.showSignatureExplanationModal();
  }

  openStashWithdrawal(): void {
    this.stateSvc.setSideMenuActiveTab('my-bids');
    this.stateSvc.setMenuOpen(true);
    this.stateSvc.requestStashWithdrawal();
  }

  handleSignatureIndicatorClick(status: SignatureStatus): void {
    if (status !== 'unsigned') {
      return;
    }
    this.web3Svc.showSignatureExplanationModal();
  }

  trackByDelegation(index: number, delegation: any): string {
    return `${delegation.type}-${delegation.from}-${delegation.to}-${delegation.contract}-${delegation.tokenId}`;
  }

  private async refreshStashWithdrawalAmount(walletAddress: string): Promise<void> {
    try {
      const stashAddress = await this.web3Svc.getStashAddressFor(walletAddress);
      const stashBalanceWei = await this.web3Svc.getStashAvailableEth(stashAddress);
      this.hasStashWithdrawal = stashBalanceWei > 0n;
      this.stashWithdrawalAmountDisplay = Number(formatEther(stashBalanceWei)).toFixed(4);
    } catch (error) {
      this.stashWithdrawalAmountDisplay = '0.0000';
      this.hasStashWithdrawal = false;
    }
  }

  private buildDelegationTooltipContent(incoming: any[], outgoing: any[]): string {
    // Compter les adresses uniques pour chaque section
    const incomingUniqueAddresses = new Set(incoming.map(d => d.from.toLowerCase()));
    const outgoingUniqueAddresses = new Set(outgoing.map(d => d.to.toLowerCase()));

    let content = '<div class="delegation-tooltip">';

    // Incoming delegations section
    if (incomingUniqueAddresses.size > 0) {
      content += `<div class="delegation-section">
        <strong>Delegated from ${incomingUniqueAddresses.size} unique address${incomingUniqueAddresses.size > 1 ? 'es' : ''}:</strong>
        <div class="wallet-list">`;

      for (const address of incomingUniqueAddresses) {
        const shortAddress = address.includes('.eth')
          ? address
          : `${address.substr(0, 6)}...${address.substr(-4)}`;
        const fullAddress = address;
        content += `<div class="wallet-item"><a href="javascript:void(0)" onclick="window.angularRouter.navigate(['/user', '${fullAddress}'])">${shortAddress}</a></div>`;
      }

      content += '</div></div>';
    } else {
      content += '<div class="delegation-section"><div class="no-delegations">No incoming delegations</div></div>';
    }

    // Outgoing delegations section
    if (outgoingUniqueAddresses.size > 0) {
      content += `<div class="delegation-section">
        <strong>Delegate to ${outgoingUniqueAddresses.size} unique address${outgoingUniqueAddresses.size > 1 ? 'es' : ''}:</strong>
        <div class="wallet-list">`;

      for (const address of outgoingUniqueAddresses) {
        const shortAddress = address.includes('.eth')
          ? address
          : `${address.substr(0, 6)}...${address.substr(-4)}`;
        const fullAddress = address;
        content += `<div class="wallet-item"><a href="javascript:void(0)" onclick="window.angularRouter.navigate(['/user', '${fullAddress}'])">${shortAddress}</a></div>`;
      }

      content += '</div></div>';
    } else {
      content += '<div class="delegation-section"><div class="no-delegations">No outgoing delegations</div></div>';
    }

    content += '  <div style="font-size: 0.9rem; margin-top: 1rem;">Manage your delegations on <a href="https://www.delegate.xyz/" style="color:white;" target="_blank">delegate.xyz</a></div>';
    content += '</div>';
    return content;
  }

  private buildSignatureStatusTooltip(status: SignatureStatus): string {
    if (status === 'signed') {
      return 'Signed: confidential bids and notification settings are enabled.';
    }

    if (status === 'unsigned') {
      return 'Connected but not signed yet. Click to sign and enable confidential bids and notification settings.';
    }

    return '';
  }
}
